import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Avatar, Modal, Spinner } from "../lawyer/LawyerUi";
import StripeCardModal from "../user/StripeCardModal";
import CallScreen from "./CallScreen";
import MediaPermissionModal from "./MediaPermissionModal";
import { ensureMediaPermission, getMediaPermissionState } from "./media";
import socketService from "../../services/SocketService";
import { apiErrorMessage, userAuth, userService } from "../../services/userApi";

// A direct call rings one lawyer; give up if they don't pick up in this time.
const RING_SECONDS = 45;

const isFree = (p) => !!p && (!!p.is_free || Number(p.price) === 0);
const fmtPrice = (p) => (isFree(p) ? "Free" : `${p.currency || "USD"} ${Number(p.price)}`);

/**
 * Direct call to one lawyer, opened from their profile. A single sheet asks for
 * a short description and a call duration, then: camera/mic check →
 * POST /checkLawyerAvailability → card hold (paid plans) → POST /initiateCall →
 * socket `call-initiate`, and the lawyer answers with `call-accepted` /
 * `call-rejected`. No category or jurisdiction — that is the broadcast
 * consultation's flow (UserVideoCall), not this one.
 */
const DirectCall = ({ lawyer, profile, open, onClose }) => {
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [packages, setPackages] = useState([]);
  const [freeTaken, setFreeTaken] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [checkingMedia, setCheckingMedia] = useState(false);
  const [mediaPrompt, setMediaPrompt] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [booking, setBooking] = useState(false);

  // idle | ringing | connected
  const [phase, setPhase] = useState("idle");
  const [activeCall, setActiveCall] = useState(null);
  const phaseRef = useRef("idle");
  const callRef = useRef(null);
  const giveUpTimer = useRef(null);
  phaseRef.current = phase;
  callRef.current = activeCall;

  const lawyerName = lawyer?.company_name || lawyer?.name || "your lawyer";

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setPackagesLoading(true);
    userService
      .getCallPackages()
      .then((res) => {
        if (!active || !res?.data?.status) return;
        const list = res.data.data || [];
        const taken = !!res.data.is_free_taken;
        // The free plan must be used first; once used only paid plans are selectable.
        const selectable = (p) => (taken ? !isFree(p) : isFree(p));
        setPackages(list);
        setFreeTaken(taken);
        setSelected((cur) => (cur && list.some((p) => p.id === cur.id && selectable(p)) ? cur : null));
      })
      .catch(() => active && toast.error("Call durations couldn't be loaded."))
      .finally(() => active && setPackagesLoading(false));
    return () => {
      active = false;
    };
  }, [open]);

  /* ----- media, availability, payment ----- */

  // Settle camera + mic before charging, or the card is held for a call that can't start.
  const requestMedia = async () => {
    setCheckingMedia(true);
    const media = await ensureMediaPermission();
    setCheckingMedia(false);
    if (media.ok) {
      setMediaPrompt(null);
      return true;
    }
    setMediaPrompt({ reason: media.reason, state: await getMediaPermissionState() });
    return false;
  };

  const proceed = async () => {
    if (!selected || !description.trim()) return;
    if (!(await requestMedia())) return;
    // A direct call can only go to a free lawyer — find out before holding the card.
    setBooking(true);
    try {
      const res = await userService.checkLawyerAvailability(lawyer.id);
      if (!res?.data?.status) {
        toast.info(res?.data?.message || "This lawyer is on another call right now.");
        return;
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't reach this lawyer right now."));
      return;
    } finally {
      setBooking(false);
    }
    if (isFree(selected)) {
      startCall("", "free");
    } else {
      onClose();
      setPayOpen(true);
    }
  };

  /* ----- ringing ----- */

  const startCall = async (transactionId, paymentStatus) => {
    setBooking(true);
    try {
      const res = await userService.initiateCall({
        lawyer_id: lawyer.id,
        call_package_id: selected.id,
        case_description: description,
        amount_paid: selected.price,
        currency: selected.currency || "USD",
        transaction_id: transactionId,
        payment_status: paymentStatus,
      });
      if (!res?.data?.status) throw new Error(res?.data?.message || "This lawyer can't take a call right now.");
      const d = res.data.data;
      const call = {
        callId: d.callId,
        channel: d.channel,
        creds: { appId: d.agoraAppId, channel: d.channel, token: d.token, uid: d.uid },
        lawyer: { ...(lawyer || {}), ...(d.lawyer || {}) },
        user: d.user,
        durationMinutes: Number(selected.duration_minutes) || 0,
        authToken: userAuth.getToken(),
        caseDetails: {
          description,
          plan: `${selected.duration_minutes} Minutes${selected.title ? ` · ${selected.title}` : ""}`,
        },
      };
      callRef.current = call;
      setActiveCall(call);
      setPhase("ringing");
      phaseRef.current = "ringing";
      onClose();
      socketService.emit("user-connected", { id: profile?.id, name: profile?.name });
      socketService.emit("call-initiate", {
        callId: d.callId,
        channel: d.channel,
        lawyer_id: Number(lawyer.id),
        user: d.user,
        durationMins: Number(selected.duration_minutes) || 0,
      });
      clearTimeout(giveUpTimer.current);
      giveUpTimer.current = setTimeout(giveUp, RING_SECONDS * 1000);
    } catch (err) {
      // Release anything already booked so a held card isn't left hanging.
      const c = callRef.current;
      if (c) userService.cancelCall(c.callId).catch(() => {});
      toast.error(apiErrorMessage(err, err?.message || "Could not start the call."));
      resetCall();
    } finally {
      setBooking(false);
    }
  };

  /* ----- call lifecycle ----- */

  const resetCall = () => {
    clearTimeout(giveUpTimer.current);
    callRef.current = null;
    setActiveCall(null);
    setPhase("idle");
    phaseRef.current = "idle";
  };

  const applyAccepted = (payloadLawyer) => {
    clearTimeout(giveUpTimer.current);
    const c = callRef.current;
    if (!c) return;
    const next = { ...c, lawyer: { ...(c.lawyer || {}), ...(payloadLawyer || {}) } };
    callRef.current = next;
    setActiveCall(next);
    setPhase("connected");
    phaseRef.current = "connected";
  };

  // Stop ringing and release the card hold. The lawyer can accept at the same
  // instant; the socket ack tells us if we lost that race, and then we join the
  // call instead of abandoning them in it.
  const release = (onDone) => {
    clearTimeout(giveUpTimer.current);
    const c = callRef.current;
    if (!c) return onDone();
    let settled = false;
    const stillOurs = () => callRef.current?.callId === c.callId && phaseRef.current === "ringing";
    const teardown = () => {
      userService.cancelCall(c.callId).catch(() => {});
      onDone();
    };
    socketService.emit("call-cancel", { callId: c.callId }, (ack) => {
      if (settled) return;
      settled = true;
      if (ack?.status === "already-accepted") {
        if (stillOurs()) applyAccepted(null);
        return;
      }
      if (stillOurs()) teardown();
    });
    setTimeout(() => {
      if (settled) return;
      settled = true;
      if (stillOurs()) teardown();
    }, 4000);
    return undefined;
  };

  const cancelRinging = () => release(resetCall);

  const giveUp = () => {
    if (phaseRef.current !== "ringing") return;
    release(() => {
      resetCall();
      toast.info("The lawyer didn't answer. You haven't been charged.");
    });
  };

  const hangUp = () => {
    const c = callRef.current;
    if (c) {
      socketService.emit("call-end", { callId: c.callId });
      userService.endCall(c.callId).catch(() => {});
    }
    resetCall();
    navigate("/user/call-history");
  };

  useEffect(() => {
    const ours = (p) => p?.callId && p.callId === callRef.current?.callId;
    const offs = [
      socketService.subscribe("call-accepted", (p) => {
        if (phaseRef.current !== "ringing" || !ours(p)) return;
        applyAccepted(p?.lawyer);
      }),
      socketService.subscribe("call-rejected", (p) => {
        if (phaseRef.current !== "ringing" || !ours(p)) return;
        resetCall();
        toast.info("The lawyer is unable to take your call right now. You haven't been charged.");
      }),
      socketService.subscribe("call-unavailable", (p) => {
        if (phaseRef.current !== "ringing" || !ours(p)) return;
        release(resetCall);
        toast.info("The lawyer is no longer available.");
      }),
      socketService.subscribe("call-ended", () => {
        if (phaseRef.current !== "connected") return;
        // The lawyer hung up — report it too so the call is settled either way.
        const c = callRef.current;
        if (c) userService.endCall(c.callId).catch(() => {});
        toast.info("The call has ended.");
        resetCall();
        navigate("/user/call-history");
      }),
    ];
    return () => offs.forEach((off) => off && off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(giveUpTimer.current), []);

  /* ----- render ----- */

  const busy = checkingMedia || booking;

  return (
    <>
      <Modal
        open={open}
        onClose={busy ? undefined : onClose}
        className="usp-dc-modal"
        title={
          <>
            Choose Call Duration
            <small>Select how long you want to talk with {lawyerName}</small>
          </>
        }
        footer={
          <button
            type="button"
            className="usp-buy-btn dark"
            onClick={proceed}
            disabled={!selected || !description.trim() || busy}
          >
            {busy ? <span className="spinner-border spinner-border-sm" /> : "Continue"}
          </button>
        }
      >
        <label className="usp-dc-label" htmlFor="usp-dc-desc">
          Description <span className="usp-req">*</span>
        </label>
        <textarea
          id="usp-dc-desc"
          className="usp-textarea usp-dc-desc"
          placeholder="Write a short description of your legal issue..."
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {packagesLoading ? (
          <Spinner />
        ) : packages.length === 0 ? (
          <p className="lwp-empty">No call durations are available right now.</p>
        ) : (
          <div className="usp-dc-plans" role="radiogroup" aria-label="Call duration">
            {packages.map((p) => {
              const free = isFree(p);
              const disabled = freeTaken ? free : !free;
              const active = selected?.id === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  role="radio"
                  aria-checked={active}
                  className={`usp-dc-plan${active ? " selected" : ""}`}
                  onClick={() => setSelected(p)}
                  disabled={disabled}
                >
                  <i className="bi bi-clock" />
                  <span className="usp-dc-plan-name">
                    <strong>{p.duration_minutes} Minutes</strong>
                    {p.title && <small>({p.title})</small>}
                    {disabled && <small className="usp-dc-plan-note">{free ? "Already used" : "Use free first"}</small>}
                  </span>
                  <span className="usp-dc-plan-price">{fmtPrice(p)}</span>
                  <span className="usp-dc-radio" />
                </button>
              );
            })}
          </div>
        )}
      </Modal>

      <StripeCardModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Confirm & Pay"
        amountLabel={selected ? fmtPrice(selected) : ""}
        submitLabel="Authorize & Call"
        billing={{ name: profile?.name, email: profile?.email }}
        note="Your card is only held now — charged after your call ends."
        prepare={() => userService.consultationPayment(selected.price, selected.id)}
        onPaid={(pi) => startCall(pi.id, pi.status === "requires_capture" ? "authorized" : "paid")}
      />

      {mediaPrompt && (
        <MediaPermissionModal
          reason={mediaPrompt.reason}
          state={mediaPrompt.state}
          onRetry={async () => {
            const ok = await requestMedia();
            if (ok) proceed();
            return ok;
          }}
          onCancel={() => setMediaPrompt(null)}
        />
      )}

      {phase === "ringing" && activeCall && (
        <div className="lwp usp-ringing" role="dialog" aria-modal="true" aria-label="Calling lawyer">
          <Avatar src={activeCall.lawyer?.picture} alt={activeCall.lawyer?.name} size={120} className="usp-ringing-avatar" />
          <h2>{activeCall.lawyer?.company_name || activeCall.lawyer?.name || "Lawyer"}</h2>
          <p>Calling…</p>
          <button type="button" className="usp-ringing-end" onClick={cancelRinging} aria-label="Cancel call">
            <i className="bi bi-telephone-x-fill" />
          </button>
        </div>
      )}

      {phase === "connected" && activeCall && <CallScreen call={activeCall} onEnd={hangUp} />}
    </>
  );
};

export default DirectCall;
